/**
 * Generate a thumbnail for an image file using the Canvas API.
 * Returns a JPEG blob, or null if generation fails.
 */
export async function generateThumbnail(
  file: File,
  maxDimension: number = 200,
  quality: number = 0.7
): Promise<Blob | null> {
  if (!file.type.startsWith('image/')) return null;

  return new Promise<Blob | null>((resolve) => {
    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    const timeout = setTimeout(() => {
      URL.revokeObjectURL(img.src);
      resolve(null);
    }, 10000);

    img.onload = () => {
      clearTimeout(timeout);
      if (!ctx) {
        URL.revokeObjectURL(img.src);
        resolve(null);
        return;
      }

      const ratio = Math.min(maxDimension / img.width, maxDimension / img.height, 1);
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);

      canvas.toBlob(
        (blob) => {
          URL.revokeObjectURL(img.src);
          resolve(blob);
        },
        'image/jpeg',
        quality
      );
    };

    img.onerror = () => {
      clearTimeout(timeout);
      URL.revokeObjectURL(img.src);
      resolve(null);
    };

    img.src = URL.createObjectURL(file);
  });
}
